import type { NavigationMenuItem } from "@nuxt/ui";

// Structure du dropdown Plateforme avec 3 sections
//
// `label`/`description`/`title` restent le texte FRANÇAIS D'ORIGINE : ce sont
// aussi des clés de correspondance pour choisir l'icône animée (voir
// AppHeaderNavIcon.vue / AppHeaderMobileIcon.vue, qui comparent `label` à des
// chaînes françaises en dur) — on n'y touche pas (lot i18n O3, 2026-10-10).
// `titreCle`/`labelCle`/`descriptionCle` sont les clés de traduction pour le
// texte AFFICHÉ (AppHeader.vue, AppHeaderDropdownPanel.vue).
export interface DropdownSection {
  title: string;
  titreCle: string;
  items: {
    label: string;
    labelCle: string;
    description: string;
    descriptionCle: string;
    icon: string;
    to: string;
    target?: string;
  }[];
}

export const platformDropdownSections: DropdownSection[] = [
  {
    title: "Collecte",
    titreCle: "navigation.appHeader.sections.collecte.titre",
    items: [
      {
        label: "Cagnotte",
        labelCle: "navigation.appHeader.sections.collecte.cagnotte.libelle",
        description: "Creez des cagnottes pour vos projets",
        descriptionCle: "navigation.appHeader.sections.collecte.cagnotte.description",
        icon: "i-lucide-piggy-bank",
        to: "/cagnotte",
      },
      {
        label: "QR Codes",
        labelCle: "navigation.appHeader.sections.collecte.qrCodes.libelle",
        description: "Generez des QR codes pour vos dons",
        descriptionCle: "navigation.appHeader.sections.collecte.qrCodes.description",
        icon: "i-lucide-qr-code",
        to: "/qr-codes",
      },
      {
        label: "Site internet",
        labelCle: "navigation.appHeader.sections.collecte.siteInternet.libelle",
        description: "La page internet gratuite de votre association",
        descriptionCle: "navigation.appHeader.sections.collecte.siteInternet.description",
        icon: "i-lucide-globe",
        to: "/site-internet",
      },
      {
        label: "Liens de paiement",
        labelCle: "navigation.appHeader.sections.collecte.liensDePaiement.libelle",
        description: "Partagez des liens pour collecter",
        descriptionCle: "navigation.appHeader.sections.collecte.liensDePaiement.description",
        icon: "i-lucide-link",
        to: "/lien-de-paiement",
      },
      {
        label: "Tap to Pay",
        labelCle: "navigation.appHeader.sections.collecte.tapToPay.libelle",
        description: "Paiement sans contact sur iPhone",
        descriptionCle: "navigation.appHeader.sections.collecte.tapToPay.description",
        icon: "i-lucide-smartphone-nfc",
        to: "/tap-to-pay",
      },
      {
        label: "Billetterie",
        labelCle: "navigation.appHeader.sections.collecte.billetterie.libelle",
        description: "Vendez et gérez vos billets en ligne",
        descriptionCle: "navigation.appHeader.sections.collecte.billetterie.description",
        icon: "i-lucide-calendar-days",
        to: "/billetterie",
      },
      {
        label: "Boutique en ligne",
        labelCle: "navigation.appHeader.sections.collecte.boutiqueEnLigne.libelle",
        description: "Vendez t-shirts, chocolats, calendriers",
        descriptionCle: "navigation.appHeader.sections.collecte.boutiqueEnLigne.description",
        icon: "i-lucide-shopping-bag",
        to: "/boutique",
      },
      {
        label: "Adhésion",
        labelCle: "navigation.appHeader.sections.collecte.adhesion.libelle",
        description: "Gestion des membres et cotisations",
        descriptionCle: "navigation.appHeader.sections.collecte.adhesion.description",
        icon: "i-lucide-id-card",
        to: "/adhesion",
      },
    ],
  },
  {
    title: "Outils",
    titreCle: "navigation.appHeader.sections.outils.titre",
    items: [
      // Déplacée de « Collecte » vers « Outils » (Karim 2026-08-23).
      {
        label: "Page de Don",
        labelCle: "navigation.appHeader.sections.outils.pageDeDon.libelle",
        description: "Page personnalisee pour vos dons",
        descriptionCle: "navigation.appHeader.sections.outils.pageDeDon.description",
        icon: "i-lucide-palette",
        to: "/page-don",
      },
      {
        label: "Dashboard",
        labelCle: "navigation.appHeader.sections.outils.dashboard.libelle",
        description: "Suivez vos collectes en temps réel",
        descriptionCle: "navigation.appHeader.sections.outils.dashboard.description",
        icon: "i-lucide-bar-chart-3",
        to: "/dashboard",
      },
      {
        label: "Reçus Fiscaux",
        labelCle: "navigation.appHeader.sections.outils.recusFiscaux.libelle",
        description: "Génération automatique de reçus",
        descriptionCle: "navigation.appHeader.sections.outils.recusFiscaux.description",
        icon: "i-lucide-file-text",
        to: "/recus-fiscaux",
      },
      {
        label: "Bornes de Don",
        labelCle: "navigation.appHeader.sections.outils.bornesDeDon.libelle",
        description: "Terminaux de paiement physiques",
        descriptionCle: "navigation.appHeader.sections.outils.bornesDeDon.description",
        icon: "i-lucide-credit-card",
        to: "/bornes-de-don",
      },
      // {
      //   label: "Tarifs",
      //   description: "Nos offres et tarification",
      //   icon: "i-lucide-tag",
      //   to: "/tarifs",
      // },
      {
        label: "Comparateurs",
        labelCle: "navigation.appHeader.sections.outils.comparateurs.libelle",
        description: "Comparer Donoor à d'autres plateformes",
        descriptionCle: "navigation.appHeader.sections.outils.comparateurs.description",
        icon: "i-lucide-scale",
        to: "/comparateurs",
      },
      {
        label: "Projets",
        labelCle: "navigation.appHeader.sections.outils.projets.libelle",
        description: "Inspirations pour vos collectes",
        descriptionCle: "navigation.appHeader.sections.outils.projets.description",
        icon: "i-lucide-lightbulb",
        to: "/projets",
      },
      {
        label: "Blog",
        labelCle: "navigation.appHeader.sections.outils.blog.libelle",
        description: "Actualités et conseils",
        descriptionCle: "navigation.appHeader.sections.outils.blog.description",
        icon: "i-lucide-newspaper",
        to: "/blog",
      },
      {
        label: "Contact",
        labelCle: "navigation.appHeader.sections.outils.contact.libelle",
        description: "Contactez notre équipe",
        descriptionCle: "navigation.appHeader.sections.outils.contact.description",
        icon: "i-lucide-mail",
        to: "/contact",
      },
    ],
  },
  {
    title: "Découvrir",
    titreCle: "navigation.appHeader.sections.decouvrir.titre",
    items: [
      // Formations gratuites (Karim 2026-10-18), modèle info.helloasso.com/formations.
      // En tête de « Découvrir » (Karim 2026-10-19) : bien visible, et « Outils »
      // garde la même hauteur que « Collecte » (8 lignes). Clés de traduction
      // inchangées (sections.outils.formations).
      {
        label: "Formations",
        labelCle: "navigation.appHeader.sections.outils.formations.libelle",
        description: "Tutoriels vidéo et fiches pratiques",
        descriptionCle: "navigation.appHeader.sections.outils.formations.description",
        icon: "i-lucide-graduation-cap",
        to: "/formations",
      },
      // Centre d'aide (Karim 2026-10-24). Avec lui, « Découvrir » passe à 8 lignes
      // comme « Collecte » et « Outils ».
      {
        label: "Aide",
        labelCle: "navigation.appHeader.sections.decouvrir.aide.libelle",
        description: "Toutes les réponses à vos questions",
        descriptionCle: "navigation.appHeader.sections.decouvrir.aide.description",
        icon: "i-lucide-life-buoy",
        to: "/help",
      },
      {
        label: "Associations",
        labelCle: "navigation.appHeader.sections.decouvrir.associations.libelle",
        description: "Découvrez les associations sur Donoor",
        descriptionCle: "navigation.appHeader.sections.decouvrir.associations.description",
        icon: "i-lucide-users",
        to: "/recherche/associations",
      },
      {
        label: "Nos cagnottes",
        labelCle: "navigation.appHeader.sections.decouvrir.nosCagnottes.libelle",
        description: "Soutenez les projets en cours",
        descriptionCle: "navigation.appHeader.sections.decouvrir.nosCagnottes.description",
        icon: "i-lucide-heart",
        to: "/recherche/cagnottes",
      },
      {
        label: "Nos billetteries",
        labelCle: "navigation.appHeader.sections.decouvrir.nosBilletteries.libelle",
        description: "Réservez vos billets en quelques clics",
        descriptionCle: "navigation.appHeader.sections.decouvrir.nosBilletteries.description",
        icon: "i-lucide-calendar-days",
        to: "/recherche/billetterie",
      },
      {
        label: "Nos adhésions",
        labelCle: "navigation.appHeader.sections.decouvrir.nosAdhesions.libelle",
        description: "Rejoignez une association en tant que membre",
        descriptionCle: "navigation.appHeader.sections.decouvrir.nosAdhesions.description",
        icon: "i-lucide-id-card",
        to: "/recherche/adhesions",
      },
      {
        // Rayon boutiques (Karim 2026-08-22) : la page `/recherche/boutiques`
        // existait et fonctionnait, mais n'était liée nulle part — elle
        // n'était atteignable que par le sitemap.
        label: "Nos boutiques",
        labelCle: "navigation.appHeader.sections.decouvrir.nosBoutiques.libelle",
        description: "Achetez des produits qui soutiennent une association",
        descriptionCle: "navigation.appHeader.sections.decouvrir.nosBoutiques.description",
        icon: "i-lucide-shopping-bag",
        to: "/recherche/boutiques",
      },
      {
        label: "Nos QR codes",
        labelCle: "navigation.appHeader.sections.decouvrir.nosQrCodes.libelle",
        description: "Découvrez les QR codes de collecte",
        descriptionCle: "navigation.appHeader.sections.decouvrir.nosQrCodes.description",
        icon: "i-lucide-qr-code",
        to: "/qr",
      },
    ],
  },
];

/**
 * Menu « Plateforme » façon Shopify (Karim 2026-11-03). Sur ordinateur
 * (AppHeaderMegaMenu) : petites sections titrées, deux par colonne au plus, et
 * une ligne d'aide en bas. Sur mobile (AppHeaderMenuMobile) : les mêmes
 * sections, en volets dépliables. `platformDropdownSections` reste la liste
 * de référence des liens et sert encore aux anciens menus gardés en secours.
 *
 * Les liens ne sont pas recopiés : chacun est repris de
 * `platformDropdownSections` par son `label`. Un `label` inconnu casse le
 * chargement de la page : une faute de frappe se voit en dev et fait échouer
 * le build, elle n'arrive jamais en ligne. Le test unitaire vérifie qu'aucun
 * lien n'est oublié ni montré deux fois.
 *
 * Icônes propres à ce menu, style Shopify (trait fin, icônes composées) :
 * Hugeicons, licence MIT (https://hugeicons.com), copiées une à une dans
 * app/assets/icons/menu-*.svg → `i-custom-menu-*`. Karim 2026-11-03.
 */
export interface MegaMenuLien {
  label: string;
  labelCle: string;
  descriptionCle: string;
  icon: string;
  to: string;
  target?: string;
}

export interface MegaMenuSection {
  titreCle: string;
  liens: MegaMenuLien[];
}

const liensParLabel = new Map(
  platformDropdownSections.flatMap((section) => section.items).map((item) => [item.label, item]),
);

function lienMegaMenu(label: string, icone: string): MegaMenuLien {
  const item = liensParLabel.get(label);
  if (!item) throw new Error(`Menu Plateforme : aucun lien « ${label} » dans platformDropdownSections`);
  return {
    label: item.label,
    labelCle: item.labelCle,
    descriptionCle: item.descriptionCle,
    icon: icone,
    to: item.to,
    target: item.target,
  };
}

const sectionCollecterDons: MegaMenuSection = {
  titreCle: "navigation.appHeader.megaMenu.collecterDons",
  liens: [
    lienMegaMenu("Cagnotte", "i-custom-menu-cagnotte"),
    lienMegaMenu("Page de Don", "i-custom-menu-page-don"),
    lienMegaMenu("Liens de paiement", "i-custom-menu-lien-paiement"),
    lienMegaMenu("QR Codes", "i-custom-menu-qr-code"),
    lienMegaMenu("Tap to Pay", "i-custom-menu-tap-to-pay"),
    lienMegaMenu("Bornes de Don", "i-custom-menu-borne"),
  ],
};

// Billetterie, boutique et adhésion font partie de la gestion de
// l'association : l'ancienne section « Vendre » y est fondue (Karim
// 2026-11-03), sur ordinateur comme sur mobile.
const sectionGererAssociation: MegaMenuSection = {
  titreCle: "navigation.appHeader.megaMenu.gererAssociation",
  liens: [
    lienMegaMenu("Dashboard", "i-custom-menu-dashboard"),
    lienMegaMenu("Reçus Fiscaux", "i-custom-menu-recu-fiscal"),
    lienMegaMenu("Site internet", "i-custom-menu-site-internet"),
    lienMegaMenu("Billetterie", "i-custom-menu-billetterie"),
    lienMegaMenu("Boutique en ligne", "i-custom-menu-boutique"),
    lienMegaMenu("Adhésion", "i-custom-menu-adhesion"),
  ],
};

const sectionSInspirer: MegaMenuSection = {
  titreCle: "navigation.appHeader.megaMenu.sInspirer",
  liens: [
    lienMegaMenu("Projets", "i-custom-menu-projets"),
    lienMegaMenu("Blog", "i-custom-menu-blog"),
    lienMegaMenu("Comparateurs", "i-custom-menu-comparateurs"),
  ],
};

const sectionDecouvrir: MegaMenuSection = {
  titreCle: "navigation.appHeader.sections.decouvrir.titre",
  liens: [
    lienMegaMenu("Associations", "i-custom-menu-associations"),
    lienMegaMenu("Nos cagnottes", "i-custom-menu-nos-cagnottes"),
    lienMegaMenu("Nos billetteries", "i-custom-menu-nos-billetteries"),
    lienMegaMenu("Nos adhésions", "i-custom-menu-nos-adhesions"),
    lienMegaMenu("Nos boutiques", "i-custom-menu-nos-boutiques"),
    lienMegaMenu("Nos QR codes", "i-custom-menu-nos-qr-codes"),
  ],
};

/**
 * Trois colonnes, chacune faite d'une ou deux sections. « S'inspirer », la
 * plus courte, complète la colonne de gauche, sous « Collecter des dons »
 * (Karim 2026-11-04).
 */
export const megaMenuColonnes: MegaMenuSection[][] = [
  [sectionCollecterDons, sectionSInspirer],
  [sectionGererAssociation],
  [sectionDecouvrir],
];

/**
 * Ligne du bas : titre à gauche, puis trois liens avec leur description. Pas
 * d'icône affichée sur cette ligne (comme chez Shopify) ; le menu mobile, lui,
 * leur en donne une (voir `menuMobileVolets`).
 */
export const megaMenuAide: MegaMenuSection = {
  titreCle: "navigation.appHeader.megaMenu.besoinAide",
  liens: ["Aide", "Formations", "Contact"].map((label) =>
    lienMegaMenu(label, liensParLabel.get(label)?.icon ?? ""),
  ),
};

/**
 * Menu « Choisir Donoor » de l'en-tête, refait sur « Choisir Shopify »
 * (Karim 2026-11-04) : trois grandes cartes à image, puis la colonne
 * « Inclus pour toutes les associations ». Textes :
 * `navigation.appHeader.choisir.<cle>Titre` / `<cle>Texte` / `<cle>Alt`.
 * L'icône sert au menu mobile, où ces cartes deviennent des liens.
 */
export interface CarteChoisirDonoor {
  cle: "lancer" | "passer" | "confiance" | "tapToPay" | "assistant";
  /** Adresse ; absente pour l'assistant, qui ouvre le chat du site. */
  to?: string;
  icon: string;
}

export const choisirDonoorCartes: CarteChoisirDonoor[] = [
  { cle: "lancer", to: "/formations", icon: "i-custom-menu-lancer" },
  { cle: "passer", to: "/comparateurs", icon: "i-custom-menu-changer" },
  { cle: "confiance", to: "/recherche/associations", icon: "i-custom-menu-confiance" },
];

/** Colonne de droite. Tap to Pay y est (Karim 2026-11-04 : retiré de la barre). */
export const choisirDonoorInclus: CarteChoisirDonoor[] = [
  { cle: "tapToPay", to: "/tap-to-pay", icon: "i-custom-menu-tap-to-pay" },
  { cle: "assistant", icon: "i-custom-menu-assistant" },
];

/**
 * Menu MOBILE (Karim 2026-11-03) : un volet par grand usage, qui se déplie au
 * toucher, un seul ouvert à la fois. Les sections de l'ordinateur sont
 * reprises telles quelles ; « S'inspirer » et « Besoin d'aide ? », trop
 * courtes seules et du même ordre (apprendre, se faire aider), forment le
 * volet « Ressources ». Sur mobile, chaque lien a son icône, aide comprise.
 */
export const menuMobileVolets: MegaMenuSection[] = [
  sectionCollecterDons,
  sectionGererAssociation,
  sectionDecouvrir,
  {
    titreCle: "navigation.appHeader.megaMenu.ressources",
    liens: [
      lienMegaMenu("Aide", "i-custom-menu-aide"),
      lienMegaMenu("Formations", "i-custom-menu-formations"),
      ...sectionSInspirer.liens,
      lienMegaMenu("Contact", "i-custom-menu-contact"),
    ],
  },
];

export const navLinks: NavigationMenuItem[] = [
  {
    label: "Plateforme",
    icon: "i-lucide-layout-grid",
    children: [
      {
        label: "Vue d'ensemble",
        description: "Découvrez toutes nos fonctionnalités",
        icon: "i-lucide-sparkles",
        to: "/logiciel-association",
      },
      {
        label: "Dashboard",
        description: "Suivez vos collectes en temps réel",
        icon: "i-lucide-bar-chart-3",
        to: "/dashboard",
      },
      {
        label: "Page de Don",
        description: "Page personnalisée pour vos dons",
        icon: "i-lucide-palette",
        to: "/page-don",
      },
    ],
  },
  {
    label: "QR Codes",
    icon: "i-lucide-qr-code",
    to: "/qr-codes",
  },
  {
    label: "Cagnotte",
    icon: "i-lucide-piggy-bank",
    to: "/cagnotte",
  },
  {
    label: "Liens de paiement",
    icon: "i-lucide-link",
    to: "/lien-de-paiement",
  },
];
